/**
 * App boot. Unauthenticated → Login; authenticated → the full application
 * (lazy AuthedApp). Wraps everything in an ErrorBoundary and mounts the ToastHost
 * (failed API calls surface as toasts). Theme (light/dark) comes from the
 * persisted theme store. A 401 on any authenticated call drops the in-memory
 * session and returns to Login (state/auth.ts).
 */
import { lazy, Suspense, useEffect } from 'react';
import { FluentProvider, Spinner } from '@fluentui/react-components';
import { lightTheme, darkTheme } from './theme';
import { LoginPage } from './auth/LoginPage';
import { ErrorBoundary } from './app/ErrorBoundary';
import { ToastHost } from './app/ToastHost';
import { useAuthStore, installAuthInterceptor } from './state/auth';
import { useThemeStore } from './state/theme';

// The authenticated app (router, TanStack Query, AppShell, all surfaces) is a
// lazy chunk, so the unauthenticated initial load is only Login + React + Fluent.
const AuthedApp = lazy(() => import('./app/AuthedApp'));

function BootFallback() {
  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
      <Spinner label="Loading…" />
    </div>
  );
}

export function App() {
  const status = useAuthStore((s) => s.status);
  const mode = useThemeStore((s) => s.mode);
  useEffect(() => installAuthInterceptor(), []);

  return (
    <FluentProvider theme={mode === 'dark' ? darkTheme : lightTheme}>
      <ErrorBoundary>
        {status === 'authenticated' ? (
          <Suspense fallback={<BootFallback />}>
            <AuthedApp />
          </Suspense>
        ) : (
          <LoginPage />
        )}
        <ToastHost />
      </ErrorBoundary>
    </FluentProvider>
  );
}

export default App;
